import { Column, Row } from 'app/design/primitives/box';
import { Page } from 'app/design/primitives/page';
import { Text } from 'app/design/primitives/text';
import useMapRadius from 'app/hooks/use-map-radius';
import { MAX_RADIUS_IN_METERS } from 'app/network/preferences';
import { IconChevronRight, IconTrash } from '@tabler/icons-react-native';
import { MapAndRadius } from 'app/atoms/map-sheet';
import { withErrorBoundary } from 'app/components/error-boundary';
import { AddressCard } from 'app/components/profile/my-information/address-card';
import { BottomSheet } from 'app/design/primitives/bottom-sheet';
import { ControlledInput } from 'app/design/primitives/input/controlled';
import { CountrySelect } from 'app/design/primitives/select/country';
import { Separator } from 'app/design/primitives/text-divider';
import { useForm } from 'app/hooks/form';
import { useScopedTranslation } from 'app/hooks/i18n';
import { useNavigationOptions } from 'app/hooks/navigation';
import type { SearchCenter } from 'app/hooks/use-search-center';
import { Event, track } from 'app/lib/analytics';
import { DEFAULT_COUNTRY } from 'app/lib/language';
import { useAllAddresses } from 'app/network/address';
import {
  type CreateAddressDTO,
  useCreateAddress,
  useDeleteAddress,
  useUpdateAddress,
} from 'app/network/address-mutations';
import { useFindUsers } from 'app/network/user';
import type { NavigationScreen } from 'app/types/navigation';
import { type PickOptional } from 'app/types/utility';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import { isNotWhiteLabel, isWhiteLabel } from 'app/utils/helpers';
import { useRegisterLoading } from 'app/utils/performance/screen-perf-context';
import { withOptionalMetrics } from 'app/utils/performance/with-metrics';
import { usePageTitle } from 'app/utils/web-helpers';
import { useEffect, useEffectEvent, useState } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Pressable, View } from 'react-native';
import * as yup from 'yup';
import { buildHeaderTitle, theme, useSelectedAddressIcon } from './my-neighbourhood/shared';
import { NewAddressHelp } from './my-neighbourhood/new-address-help';
import { MembersLink } from './my-neighbourhood/members-link';
import { AddressIconPicker } from './my-neighbourhood/address-icon-picker';
import { NeighbourhoodActions } from './my-neighbourhood/neighbourhood-actions';

function getNeighbourhoodPresentation({
  createPending,
  deletePending,
  formIsLoading,
  formIsValid,
  hasAddressId,
  listUsersFetching,
  selectedAddress,
  updatePending,
}: {
  createPending: boolean;
  deletePending: boolean;
  formIsLoading: boolean;
  formIsValid: boolean;
  hasAddressId: boolean;
  listUsersFetching: boolean;
  selectedAddress: { street?: string | null } | undefined;
  updatePending: boolean;
}) {
  const hasSelectedAddress = Boolean(selectedAddress);
  const hasStreet = Boolean(selectedAddress?.street?.trim());
  return {
    deleteStatus: deletePending ? ('pending' as const) : ('idle' as const),
    isLoadingMembers: listUsersFetching || updatePending,
    mapVisibility: hasSelectedAddress ? ('visible' as const) : ('hidden' as const),
    saveState: {
      availability: formIsValid ? ('enabled' as const) : ('disabled' as const),
      status: formIsLoading || createPending || updatePending ? ('pending' as const) : ('idle' as const),
    },
    showAddressCard: hasStreet,
    showCompleteAddressLink: hasSelectedAddress,
    showCountrySelect: isNotWhiteLabel(),
    showNewAddressHelp: !hasAddressId,
  };
}

const MyNeighbourhoodScreenComponent: NavigationScreen<'my-neighbourhood'> = ({ navigation, route }) => {
  const { t } = useScopedTranslation();
  const { t: tAPI } = useTranslation('api-errors');

  const addressId = route.params?.addressId;
  const listUsersQuery = useFindUsers(addressId ? { addressIds: [addressId] } : undefined, { enabled: !!addressId });

  const { data: existingAddresses, isPending: isPendingAddresses } = useAllAddresses();
  const accountCountry = existingAddresses?.[0]?.country;
  const createAddress = useCreateAddress();
  const updateAddress = useUpdateAddress();
  const deleteAddress = useDeleteAddress();

  const {
    selectedAddress,
    markerPosition,
    radius,
    setRadius,
    radiusCenter,
    setRadiusCenter,
    defaults: { DEFAULT_SHARE_RADIUS_IN_METERS },
  } = useMapRadius(route.params?.addressId);

  type FormSchema = Pick<CreateAddressDTO, 'name'> &
    PickOptional<CreateAddressDTO, 'country' | 'postalCode'> & {
      radius: number;
    };

  const selectedStreet = selectedAddress?.street;

  const schema: yup.ObjectSchema<FormSchema> = yup.object({
    name: yup.string().required(),
    country: yup.string().when({
      is: () => isWhiteLabel(),
      then: (s) => s.optional().nullable(),
      otherwise: (s) => s.length(2).trim().required(),
    }),
    postalCode: yup
      .string()
      .trim()
      .max(10)
      .when({
        is: () => !selectedStreet || selectedStreet.trim().length === 0,
        then: (s) => s.min(4).required(t('postalCodeRequiredError', { ns: 'common' })),
        otherwise: (s) => s.optional().nullable(),
      }),
    radius: yup.number().min(1000).max(MAX_RADIUS_IN_METERS).required(),
  });

  const { control, formState, handleSubmit, setValue } = useForm({
    schema,
    validationMode: 'onBlur',
    defaultValues: {
      name: '',
      country: existingAddresses?.[0]?.country ?? DEFAULT_COUNTRY,
      postalCode: '',
      radius: selectedAddress?.radius || DEFAULT_SHARE_RADIUS_IN_METERS,
    },
  });

  const { selectedAddressId, selectedIcon, setSelectedIcon } = useSelectedAddressIcon(selectedAddress);

  const resetFromSelectedAddress = useEffectEvent(() => {
    if (!selectedAddress) return;
    setValue('name', selectedAddress.name ?? t('neighbourhoodClubName', { ns: 'my-clubs' }));
    setValue('country', selectedAddress.country);
    setValue('postalCode', selectedAddress.postalCode);
  });
  useEffect(() => {
    if (!selectedAddressId) return;
    resetFromSelectedAddress();
  }, [selectedAddressId]);

  useEffect(() => {
    if (!selectedAddress && accountCountry) setValue('country', accountCountry);
  }, [accountCountry, setValue, selectedAddress]);
  const onSaveAddress = handleSubmit((form) => {
    createAddress.reset();
    updateAddress.reset();
    deleteAddress.reset();
    const data = {
      name: form.name,
      icon: selectedIcon || 1,
      radius: radius.actual || DEFAULT_SHARE_RADIUS_IN_METERS,
    };
    if (selectedAddress) {
      updateAddress.mutate(
        {
          id: route.params!.addressId!,
          ...data,
          radiusCenterLatitude: radiusCenter.latitude,
          radiusCenterLongitude: radiusCenter.longitude,
          ...(!selectedAddress.street || selectedAddress.street.length === 0
            ? { postalCode: form.postalCode, country: form.country }
            : {}),
        },
        {
          onSuccess: () => {
            navigation.goBack();
          },
        },
      );
    } else {
      createAddress.mutate(
        {
          ...data,
          street: null,
          unit: null,
          city: null,
          administrativeArea: null,
          postalCode: form.postalCode!,
          country: form.country ?? existingAddresses?.[0]?.country ?? DEFAULT_COUNTRY,
          latitude: markerPosition.latitude,
          longitude: markerPosition.longitude,
          radiusCenterLatitude: null,
          radiusCenterLongitude: null,
        },
        {
          onSuccess: (newAddress) => {
            track(Event.AddressAdded, { address: newAddress.id, source: route.params!.newAddressSource! });

            navigation.goBack();
          },
        },
      );
    }
  });

  const onDeletePress = () => {
    createAddress.reset();
    updateAddress.reset();
    deleteAddress.reset();
    setShowActions(false);
    deleteAddress.mutate(selectedAddress!.id, {
      onSuccess: () => {
        track(Event.AddressDeleted, { address: selectedAddress!.id });
        navigation.goBack();
      },
    });
  };

  const [showActions, setShowActions] = useState(false);

  const headerTitle = buildHeaderTitle(t, Boolean(route.params?.addressId), selectedAddress);
  useNavigationOptions(navigation, {
    ...(navigation.getState().index === 0 ? { headerBackVisible: false } : {}), // Hide back button on Android when Close button is shown
    headerTitle,
  });

  const setPageTitle = usePageTitle();
  useEffect(() => setPageTitle(headerTitle), [headerTitle, setPageTitle]);

  useRegisterLoading(isPendingAddresses, 'MyNeighbourhoodScreen - fetching addresses');
  const presentation = getNeighbourhoodPresentation({
    createPending: createAddress.isPending,
    deletePending: deleteAddress.isPending,
    formIsLoading: formState.isLoading,
    formIsValid: formState.isValid,
    hasAddressId: Boolean(route.params?.addressId),
    listUsersFetching: listUsersQuery.isFetching,
    selectedAddress,
    updatePending: updateAddress.isPending,
  });

  return (
    <Page>
      <Column gap="spacing.general.x5">
        <NewAddressHelp visible={presentation.showNewAddressHelp} />

        <MembersLink
          addressId={addressId}
          isLoading={presentation.isLoadingMembers}
          onPress={() => navigation.navigate('group-members', { addressIds: [addressId!] }, { pop: true })}
          total={listUsersQuery.data?.pages[0]?.total}
        />

        <ControlledInput
          {...{ control }}
          autoComplete="off"
          label={t('clubNameLabel')}
          markAsRequired
          name="name"
          textContentType="name"
        />

        {presentation.showAddressCard ? (
          <AddressCard {...selectedAddress!} />
        ) : (
          <Column gap="spacing.general.x5">
            <ControlledInput
              {...{ control }}
              autoCapitalize="characters"
              autoComplete="postal-code"
              label={t('postalCodeLabel')}
              markAsRequired
              name="postalCode"
              textContentType="postalCode"
            />

            {presentation.showCountrySelect ? (
              <View style={{ marginTop: 5 }}>
                <Controller
                  {...{ control }}
                  name="country"
                  render={({ field, fieldState }) => (
                    <CountrySelect
                      accountCountry={accountCountry}
                      error={fieldState.error}
                      label={t('countryFieldLabel', { ns: 'my-address' })}
                      markAsRequired
                      onChange={field.onChange}
                      value={field.value}
                    />
                  )}
                />
                {accountCountry ? (
                  <Text color="text.tertiary" marginTop="spacing.general.x1" variant="bodySmall">
                    {t('countryRegionLockedHelp', { ns: 'my-address' })}
                  </Text>
                ) : null}
              </View>
            ) : null}

            {presentation.showCompleteAddressLink ? (
              <Pressable
                onPress={() => navigation.navigate('my-address', { id: route.params!.addressId! }, { pop: true })}
              >
                <Row
                  alignItems="center"
                  borderBottomWidth={1}
                  borderColor="text.tertiary"
                  flex={1}
                  justifyContent="space-between"
                  marginVertical="spacing.general.x1"
                  paddingBottom="spacing.general.x1"
                >
                  <Row gap="spacing.general.x1">
                    <Text color="text.brand" fontSize={16} fontWeight="700">
                      {t('completeAddress')}
                    </Text>
                  </Row>
                  <IconChevronRight strokeWidth={ICON_STROKE_WIDTH} />
                </Row>
              </Pressable>
            ) : null}
          </Column>
        )}

        <AddressIconPicker onSelect={setSelectedIcon} selectedIcon={selectedIcon} />

        <View style={{ marginBottom: theme.spacing['spacing.general.x2'] }}>
          <Separator />
        </View>

        <Text fontSize={16} fontWeight="700" variant="bodyBase">
          {t('radiusLabel')}
        </Text>

        <MapAndRadius
          initialRadius={radius.actual}
          initialRadiusCenter={{ ...radiusCenter }}
          markerPosition={markerPosition}
          onChange={(r: number, sC: SearchCenter) => {
            setRadius(r);
            setRadiusCenter({ latitude: sC.radiusCenterLatitude, longitude: sC.radiusCenterLongitude });
            setValue('radius', r, { shouldValidate: true });
          }}
          variant={{
            map: presentation.mapVisibility,
            audience:
              presentation.mapVisibility === 'visible'
                ? { type: 'neighbours', text: t('audience') }
                : { type: 'hidden' },
          }}
        />

        <NeighbourhoodActions
          addressCount={existingAddresses?.length ?? 0}
          createError={createAddress.error ?? updateAddress.error ?? deleteAddress.error}
          deleteStatus={presentation.deleteStatus}
          onDelete={() => setShowActions(true)}
          onSave={() => void onSaveAddress()}
          saveState={presentation.saveState}
          selectedAddress={selectedAddress}
          translateError={tAPI}
        />
      </Column>

      <Column style={{ height: theme.spacing['spacing.general.x5'] }} />

      <BottomSheet onClose={() => setShowActions(false)} open={showActions}>
        <BottomSheet.Action onPress={onDeletePress} prefix={<IconTrash strokeWidth={ICON_STROKE_WIDTH} />}>
          {t('deleteConfirmation')}
        </BottomSheet.Action>
      </BottomSheet>
    </Page>
  );
};

export const MyNeighbourhoodScreen: NavigationScreen<'my-neighbourhood'> = withOptionalMetrics(
  'MyNeighbourhoodScreen',
  withErrorBoundary(MyNeighbourhoodScreenComponent),
);
